import { useEffect, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Building2, Landmark, ReceiptText, Loader2, Check } from 'lucide-react';
import {
  fetchOrganizationBillingSettings,
  updateOrganizationBillingSettings,
  type OrganizationBillingSettings,
} from '@/lib/api';

interface BillingFormState {
  legalName: string;
  address: string;
  phone: string;
  email: string;
  website: string;
  vatNumber: string;
  registrationNumber: string;
  taxLabel: string;
  bankName: string;
  accountHolder: string;
  accountNumber: string;
  branchCode: string;
  swift: string;
}

const EMPTY_FORM: BillingFormState = {
  legalName: '',
  address: '',
  phone: '',
  email: '',
  website: '',
  vatNumber: '',
  registrationNumber: '',
  taxLabel: '',
  bankName: '',
  accountHolder: '',
  accountNumber: '',
  branchCode: '',
  swift: '',
};

function toFormState(billing: OrganizationBillingSettings): BillingFormState {
  const bank = billing.bankDetails ?? {};
  return {
    legalName: billing.legalName ?? '',
    address: billing.address ?? '',
    phone: billing.phone ?? '',
    email: billing.email ?? '',
    website: billing.website ?? '',
    vatNumber: billing.vatNumber ?? '',
    registrationNumber: billing.registrationNumber ?? '',
    taxLabel: billing.taxLabel ?? '',
    bankName: bank.bankName ?? '',
    accountHolder: bank.accountHolder ?? '',
    accountNumber: bank.accountNumber ?? '',
    branchCode: bank.branchCode ?? '',
    swift: bank.swift ?? '',
  };
}

function toPayload(form: BillingFormState): OrganizationBillingSettings {
  return {
    legalName: form.legalName,
    address: form.address,
    phone: form.phone,
    email: form.email,
    website: form.website,
    vatNumber: form.vatNumber,
    registrationNumber: form.registrationNumber,
    taxLabel: form.taxLabel,
    bankDetails: {
      bankName: form.bankName,
      accountHolder: form.accountHolder,
      accountNumber: form.accountNumber,
      branchCode: form.branchCode,
      swift: form.swift,
    },
  };
}

export function OrganizationSettings() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [form, setForm] = useState<BillingFormState>(EMPTY_FORM);
  const [loaded, setLoaded] = useState(false);
  const [saved, setSaved] = useState(false);

  const { data: billing, isLoading, isError, refetch } = useQuery({
    queryKey: ['organization-billing-settings'],
    queryFn: fetchOrganizationBillingSettings,
  });

  useEffect(() => {
    if (billing && !loaded) {
      setForm(toFormState(billing));
      setLoaded(true);
    }
  }, [billing, loaded]);

  const saveMutation = useMutation({
    mutationFn: updateOrganizationBillingSettings,
    onSuccess: (updated) => {
      queryClient.invalidateQueries({ queryKey: ['organization-billing-settings'] });
      setForm(toFormState(updated));
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    },
  });

  if (!user) return null;

  const setField =
    (key: keyof BillingFormState) =>
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
      setForm((prev) => ({ ...prev, [key]: e.target.value }));

  const handleSave = () => {
    saveMutation.mutate(toPayload(form));
  };

  const field = (
    key: keyof BillingFormState,
    label: string,
    placeholder = '',
    helper?: string,
  ) => (
    <div className="space-y-2">
      <Label htmlFor={`billing-${key}`}>{label}</Label>
      <Input
        id={`billing-${key}`}
        value={form[key]}
        onChange={setField(key)}
        placeholder={placeholder}
        disabled={!loaded}
      />
      {helper && <p className="text-xs text-text-tertiary">{helper}</p>}
    </div>
  );

  return (
    <div className="space-y-6 max-w-2xl">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Building2 className="h-5 w-5 text-emerald-400" />
            Organization Details
          </CardTitle>
          <CardDescription>View your organization information</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <p className="text-sm text-text-tertiary">Organization Name</p>
              <p className="font-medium text-lg">{user.organization.name}</p>
            </div>
            <div>
              <p className="text-sm text-text-tertiary">Organization Slug</p>
              <p className="font-medium font-mono text-text-secondary">{user.organization.slug}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {isError && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <ReceiptText className="h-5 w-5 text-red-400" />
              Invoice Billing Details
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="bg-bg-secondary rounded-lg p-6 text-center space-y-3">
              <p className="text-sm text-red-400">
                Could not load the current billing details. Editing is disabled so existing
                values are not overwritten.
              </p>
              <Button variant="outline" onClick={() => refetch()}>
                Retry
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {!isError && (
      <>
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <ReceiptText className="h-5 w-5 text-emerald-400" />
            Invoice Billing Details
          </CardTitle>
          <CardDescription>
            Legal and contact details printed on invoice PDFs and invoice emails
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin text-text-tertiary" />
            </div>
          ) : (
            <div className="grid gap-4 sm:grid-cols-2">
              {field('legalName', 'Legal Name', 'Opus Recruiter (Pty) Ltd')}
              {field('registrationNumber', 'Registration Number', '2025/000000/07')}
              <div className="space-y-2 sm:col-span-2">
                <Label htmlFor="billing-address">Address</Label>
                {/* Multi-line: each line prints as its own line on the invoice PDF. */}
                <Textarea
                  id="billing-address"
                  rows={4}
                  value={form.address}
                  onChange={setField('address')}
                  placeholder={'639 Lake Road\nGeorge\nWestern Cape 6560\nSouth Africa'}
                  disabled={!loaded}
                />
                <p className="text-xs text-text-tertiary">
                  One line per address line — printed as-is on the invoice PDF.
                </p>
              </div>
              {field('phone', 'Phone', '+27 00 000 0000')}
              {field('email', 'Email', 'info@example.com')}
              {field('website', 'Website', 'https://example.com')}
              {field(
                'vatNumber',
                'VAT Number',
                '',
                "When a VAT number is set, invoice PDFs are headed 'TAX INVOICE'. Clearing it switches the heading back to 'INVOICE'.",
              )}
              {field('taxLabel', 'Tax Label', 'VAT', 'Label used for the tax line on invoices.')}
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Landmark className="h-5 w-5 text-emerald-400" />
            Bank Details
          </CardTitle>
          <CardDescription>
            Payment details shown on invoice PDFs and in invoice emails
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {!isLoading && (
            <div className="grid gap-4 sm:grid-cols-2">
              {field('bankName', 'Bank Name', 'FNB')}
              {field('accountHolder', 'Account Holder', 'Opus Recruiter (Pty) Ltd')}
              {field('accountNumber', 'Account Number', '')}
              {field('branchCode', 'Branch Code', '')}
              {field('swift', 'SWIFT Code', '')}
            </div>
          )}
        </CardContent>
      </Card>
      </>
      )}

      {!isError && (
        <div className="flex items-center gap-3">
          <Button onClick={handleSave} disabled={!loaded || saveMutation.isPending}>
            {saveMutation.isPending ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Saving…
              </>
            ) : (
              'Save Billing Details'
            )}
          </Button>
          {saved && (
            <span className="flex items-center gap-1 text-sm text-emerald-400">
              <Check className="h-4 w-4" />
              Saved
            </span>
          )}
          {saveMutation.isError && (
            <span className="text-sm text-red-400">
              Failed to save billing details. Please try again.
            </span>
          )}
        </div>
      )}
    </div>
  );
}
